<template>
  <div class="animation-controls">
    <div class="timeline">
      <Scrubber
        v-model="frame"
        :min="1"
        :max="frameCount"
        :secondary="buffered"
        :step="1"
        :disabled="isPropagating"
        @scrubbing-end="doneScrubbing"
      >
        <template #default="{ position, pendingValue }">
          <div
            class="scrubber-popup"
            :style="{ left: position }"
          >
            {{ isPropagating ? "Playback disabled during propagation." : formatScrubberValue(pendingValue) }}
          </div>
          <div
            class="scrubber-thumb"
            :style="{ left: position }"
          />
        </template>
      </Scrubber>
    </div>
    <div class="controls">
      <div class="controls__start">
        <span v-if="frame && frameCount" class="frame-count">{{ frame }} / {{ frameCount }} <h5>{{ isImagesOnly ? 'images' : 'frames' }}</h5></span>
        <SVGIcon
          v-if="isLoadingSampleFailed"
          v-tooltip="`An error occurred while loading sample`"
          class="warning-icon ms-1"
          :iconName="'warning'"
          :width="'20px'"
          :height="'20px'"
        />
      </div>
      <div class="controls__buttons">
        <IconButton
          v-tooltip="isPropagating ? 'Playback disabled during propagation.' : 'Previous frame (Left Arrow)'"
          :icon="'skip_previous'"
          :width="24"
          :height="24"
          :type="'click'"
          :disabled="isPropagating"
          @click="handleSkipPrevious"
        />
        <IconButton
          v-model="playing"
          v-tooltip="isPropagating ? 'Playback disabled during propagation.' : playing ? 'Pause' : 'Play'"
          aria-label="Play/Pause"
          :icon="'play'"
          :iconActive="'pause'"
          :width="32"
          :height="32"
          :type="'toggle-icon'"
          :showHover="true"
          :disabled="isPropagating"
          @click="handlePlayButtonClick"
        />
        <IconButton
          v-tooltip="isPropagating ? 'Playback disabled during propagation.' : 'Next frame (Right Arrow)'"
          :icon="'skip_next'"
          :width="24"
          :height="24"
          :type="'click'"
          :disabled="isPropagating"
          @click="handleSkipNext"
        />
      </div>
      <div class="controls__end">
        <!-- <div class="annotation-search">
          <AnnotationExpandingSearch
            :labels="labels"
            @previous="handlePreviousAnnotationClicked"
            @next="handleNextAnnotationClicked"
          />
        </div> -->
        <BaseMenu
          :placement="'bottom-start'"
          :appendToBody="true"
          @closed="show_playback_menu = false"
        >
          <IconButton
            v-model="show_playback_menu"
            title="Playback Speed"
            :icon="'playback_speed'"
            :width="24"
            :height="24"
            class="ms-auto"
          />
          <template #menu>
            <div class="input-buttons scrollbar">
              <template v-for="(option, index) in playback_options" :key="index">
                <label
                  :for="`menuItem-${option.value}`"
                  class="input-buttons__row input-buttons__row-button"
                >
                  <div class="input-buttons__row-button-check me-2">
                    <SVGIcon
                      v-if="fps === option.value"
                      :iconName="'check'"
                      :height="'20px'"
                      :width="'20px'"
                    />
                  </div>
                  <input
                    :id="`menuItem-${option.value}`"
                    v-model="fps"
                    type="radio"
                    :value="option.value"
                  >
                  <span>{{ option.name }}</span>
                </label>
              </template>
            </div>
          </template>
        </BaseMenu>
        <SVGIcon
          v-if="currentAnimationSample && currentAnimationSample?.imageObj?.review_status == 'Done'"
          class="verified_badge"
          :iconName="'verified_filled'"
          :width="'24px'"
          :height="'24px'"
        />
      </div>
    </div>
    <div>
      <KeyframeTimeline2
        v-if="doKeyframesExist"
        v-model:frame="frame"
        v-model:data="keyframes"
        :frameCount="frameCount"
        class="mt-2 mb-2"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref, onMounted, onUnmounted, watch, toRefs, computed, nextTick,
} from 'vue';
import useImagesFetch from '@/composables/useImagesFetch.js';
import IconButton from '@/components/IconButton.vue';
import Scrubber from '@/components/DatasetComponent/AnnotationTool/VideoControls/Scrubber.vue';
import KeyframeTimeline2 from '@/components/DatasetComponent/AnnotationTool/KeyframeTimeline2.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import BaseMenu from '@/components/BaseMenu.vue';
import useViewerVisualizations from '@/composables/useViewerVisualizations.js';
import useGallery from '@/composables/annotationTool/useGallery.js';
import { useStore } from 'vuex';
import { useSAM2Store } from '@/stores/useSAM2Store.js';
import { storeToRefs } from 'pinia';
import { useEditorStore } from '@/stores/useEditorStore.js';

const props = defineProps({
  sequenceId: {
    type: Number,
    default: null,
  },
  datasetId: {
    type: Number,
    default: null,
  },
  reviewSettings: {
    type: Object,
    default: null,
  },
  labels: {
    type: Array,
    default: () => [],
  },
  selectedAnnotationSets: {
    type: Array,
    default: () => [],
  },
});
const {
  sequenceId,
  datasetId,
  selectedAnnotationSets,
} = toRefs(props);

// const worker = new Worker(new URL('@/workers/cacheWorker.js', import.meta.url), {
//   type: 'module',
// });

// worker.onmessage = (message) => {
//   handleCacheWorkerResults(message.data);
// };

// worker.onerror = (message) => {
//   console.log("worker error:", message);
// };

const store = useStore();

const emit = defineEmits(['samples-loaded', 'new-frame']);
defineExpose({
  handleSkipPrevious, handleSkipNext, handleClearCache, cacheImageForNewFrame,
});

const show_playback_menu = ref(false);
const playback_options = ref([
  {
    value: 1,
    name: '1 FPS',
  },
  {
    value: 5,
    name: '5 FPS',
  },
  {
    value: 10,
    name: '10 FPS',
  },
  {
    value: 20,
    name: '20 FPS',
  },
  // {
  //   value: 100,
  //   name: '100 FPS',
  // },
]);
const isImagesOnly = ref(false);

const editorStore = useEditorStore();
const {
  currentSample: currentAnimationSample,
} = storeToRefs(editorStore);
const { clearSample } = editorStore;

const {
  samples,
  animationImageCache,
  frame,
  playing,
  buffered,
  keyframes,
  doKeyframesExist,
  frameCount,
  fps,
  isInitializingAnimation,
  topics,
  cacheWorkerPool,

  cacheImages,
  processSample,
  loadSample,
  loadSampleToCache,
  abortCaching,

  startAnimation,
  pauseAnimation,
  addNewSampleToCache,
  updateSample,
  throttledUpdateSample,
  resetForNewSequence,
  $reset,
  limitAnnotationsToSet,
} = useViewerVisualizations(selectedAnnotationSets.value);

const {
  isPropagating,
} = storeToRefs(useSAM2Store());

const {
  getImagesFilterParams,
} = useGallery();

const { controller, getImages, getLabellingSequenceFrames } = useImagesFetch();

const imagesFilterParams = computed(() => getImagesFilterParams());

async function doneScrubbing() {
  playing.value = false;

  if (!isInitializingAnimation.value && frameCount.value > 0) {
    if (
      !animationImageCache.value[frame.value - 1]
        || !animationImageCache.value[frame.value - 1].loaded
    ) {
      addNewSampleToCache(frame.value - 1);
      clearSample();
      cacheWorkerPool.restartWorkers();
      await processSample({ imageId: samples.value[frame.value - 1], annotationSetId: limitAnnotationsToSet.value, indexToCache: frame.value - 1 });
      updateSample();
    }
  }
  nextTick(() => {
    cacheImages();
    emit('new-frame');
  });
}

onMounted(async () => {
  resetForNewSequence();

  loadFirstImage();
});

onUnmounted(async () => {
  abortCaching();
  $reset();
});

async function loadFirstImage() {
  if (controller.value) {
    controller.value.abort();
  }
  controller.value = new AbortController();
  const currentSignal = controller.value.signal;

  isInitializingAnimation.value = true;

  // Load sample to the currentAnimationSample
  const firstImageId = currentAnimationSample.value.imageObj.id;
  // const firstSample = {
  //   image: null,
  //   imageObj: null,
  //   annotations: [],
  //   loaded: false,
  // };
  // currentAnimationSample.value = firstSample;

  if (props.reviewSettings) {
    // Disable for now
    // limitAnnotationsToSet.value = props.reviewSettings.reviewTask.source_annotation_set_id;
  } else {
    limitAnnotationsToSet.value = null;
  }

  await processSample({ imageId: firstImageId, annotationSetId: limitAnnotationsToSet.value });
  updateSample();

  isInitializingAnimation.value = false;

  if (props.reviewSettings?.reviewTask?.id) {
    const filters = {
      images_filter: {
        sequence_id: sequenceId.value,
      },
      image_files_filter: {},
    };
    filters.images_filter.dataset_id = props.reviewSettings.reviewTask.dataset_id;
    filters.aggregate_annotations_filter = { ...filters.aggregate_annotations_filter, aggregate_annotation_set_ids: [props.reviewSettings.reviewTask.dest_annotation_set_id] };
    const options = {
      sort_by: 'id',
      reverse: false,
      offset: 0,
      source_annotation_set_id: props.reviewSettings.reviewTask.source_annotation_set_id,
      annotations_filter: null,
      aggregate_annotations_filter: { aggregate_annotation_set_ids: [props.reviewSettings.reviewTask.dest_annotation_set_id] },
      review_task_id: props.reviewSettings.reviewTask.id,
      only_ids: true,
    };
    samples.value = await getLabellingSequenceFrames(filters, options, currentSignal)
      .catch((err) => {
        console.log(err);
        throw Error(err);
      });
  } else {
    const filters = {
      ...imagesFilterParams.value,
    };
    filters.images_filter.dataset_id = datasetId.value;
    filters.images_filter.sequence_id = sequenceId.value;
    const params = {
      combine_sequence_frames: true,
      get_annotations: false,
      limit: 0,
      offset: 0,
      only_ids: true,
    };
    if (!sequenceId.value) {
      params.is_mixed = false;
      isImagesOnly.value = true;
    } else {
      isImagesOnly.value = false;
    }
    // const options = {
    //   get_annotations: false,
    //   sort_by: 'id',
    //   reverse: false,
    //   offset: 0,
    // };
    samples.value = await getImages(filters, params, currentSignal)
      .catch((err) => {
        throw Error(err);
      });
  }

  emit('samples-loaded', samples.value);

  const offset = samples.value.findIndex((e) => e === firstImageId);

  frameCount.value = samples.value.length;
  if (offset) {
    frame.value = offset + 1;
  } else {
    frame.value = 1;
  }

  if (!currentSignal.aborted) {
    nextTick(() => {
      cacheImages();
    });
  }
}

watch(frame, (newFrame, oldFrame) => {
  if (oldFrame === 0) return; // Don't let this function set the sample for first image
  if (samples.value.length > 0) {
    if (!playing.value) {
      throttledUpdateSample();
    }
  }
});

function formatScrubberValue(v) {
  return v;
}

function handlePlayButtonClick() {
  playing.value ? pauseAnimation() : startAnimation();
  nextTick(() => {
    if (!playing.value) {
      emit('new-frame');
    }
  });
}

async function handleSkipPrevious() {
  if (frame.value > 1) {
    frame.value -= 1;

    await cacheImageForNewFrame();
  }
}

async function handleSkipNext() {
  if (frame.value < samples.value.length) {
    frame.value += 1;
  } else {
    frame.value = 1;
  }
  await cacheImageForNewFrame();
}

async function cacheImageForNewFrame() {
  if (!animationImageCache.value[frame.value - 1]?.loaded) {
    addNewSampleToCache(frame.value - 1);
    clearSample();
    cacheWorkerPool.restartWorkers();
    await processSample({ imageId: samples.value[frame.value - 1], annotationSetId: limitAnnotationsToSet.value, indexToCache: frame.value - 1 });
    updateSample();
  }
  nextTick(() => {
    cacheImages();
    emit('new-frame');
  });
}

function handleClearCache() {
  animationImageCache.value = [];
}

watch(sequenceId, async () => {
  resetForNewSequence();
  loadFirstImage();
});

const isLoadingSampleFailed = computed(() => {
  if (currentAnimationSample.value?.failed) {
    return true;
  }

  return false;
});

</script>

<style lang="scss" scoped>
.animation-controls {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 70px;
  padding: 0 8px;
  z-index: 10;
  border-left: 1px solid rgba(0,0,0,0.25);
  background: var(--body-color);
}

.timeline {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 20px;
  padding: 8px 0;
}

.controls {
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 50px;

  &__start {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1 1 1px;
  }

  &__end {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1 1 1px;
    gap: 8px;
  }

  &__input {
    max-width: 100px;
  }

  &__buttons {
    max-width: 100px;
  }
}

.scrubber-popup {
  position: absolute;
  transform: translateX(-50%);
  background-color: #000;
  border-radius: 0.375rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  bottom: 0;
  margin-bottom: 1rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  font-size: 0.75rem;
  color: #fff;
  pointer-events: none;
}
.scrubber-thumb {
  position: absolute;
  transform: translate(-50%, 7px);
  background-color: var(--color-primary-400);
  height: 18px;
  width: 4px;
  bottom: 0;
}

.verified_badge {
  color: var(--color-success);
}

.annotation-search {
  padding: 4px 0px;
  margin-left: auto;
  max-width: 300px;
}

.input-buttons {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: inherit;
  text-transform: none;
  min-width: 100px;

  &.scrollbar {
    overflow: auto;
  }

  &__row {
    display: flex;
    align-items: center;
    position: relative;
    max-width: 500px;
  }

  &__row-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background: transparent;
    border: none;
    padding: 6px 8px;
    cursor: pointer;

    &:first-of-type {
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
    }
    &:last-of-type {
      border-bottom-left-radius: inherit;
      border-bottom-right-radius: inherit;
    }

    &:hover {
      background: rgba(0,0,0,0.125);
    }
    &:focus-within {
      background: rgba(0,0,0,0.125);
    }

    &> * {
      pointer-events: none;
    }

    input {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      opacity: 0;
      margin: 0;
    }

    span, label {
      font-size: 0.9rem;
      font-weight: 500;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  &__row-button-check {
    width: 20px;
    min-width: 20px;
    height: 20px;
    min-height: 20px;
    @include themify() {
      color: themed('filter-button-list-row-button-selected')
    }
  }
}

.warning-icon {
  color: var(--color-error)
}

.frame-count {
  width: fit-content;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 7px;
}
</style>
